import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import momentZ from 'moment-timezone';
import MDSpinner from 'react-md-spinner';
import {List} from 'react-window';
import {spinnerColor} from '../../utils/StyleUtils';
import {loadWebUsers} from '../../actions/users';
import {WebUserRow, WebUsersCursor} from '../../models/WebUser';

interface Props {
    users: WebUserRow[];
    isLoading: boolean;
    hasMore: boolean;
    nextCursor: WebUsersCursor | null;
    loadWebUsers: (cursor?: WebUsersCursor | null) => void;
}

// Mirrors .user-item height in _users.scss.
const ROW_HEIGHT = 36;

const formatDate = (value: string | null) => (value ? momentZ(value).format('YYYY-MM-DD HH:mm') : '—');

const Row = ({index, style, users}: {index: number, style: React.CSSProperties, users: WebUserRow[]}) => {
  const user = users[index];
  return (
    <div className={'user-item'} style={style}>
      <div className={'users-email-column'} title={user.email ?? ''}>{user.email ?? '—'}</div>
      <div className={'users-date-column'}>{formatDate(user.createdAt)}</div>
      <div className={'users-status-column'}>
        <span className={user.hasPurchase ? 'users-badge-bought' : 'users-badge-none'}>
          {user.hasPurchase ? 'bought' : 'not bought'}
        </span>
      </div>
      <div className={'users-date-column'}>{formatDate(user.statusChangedAt)}</div>
    </div>
  );
};

const UsersTable = ({users, isLoading, hasMore, nextCursor, loadWebUsers}: Props) => {
  // The first page is requested by the section wrapper, not here: React runs child effects before
  // the parent's, and in the Astify panel the socket is opened by the wrapper — a request fired
  // from this component would leave before any connection existed and be dropped silently.
  //
  // Keep pulling pages until the backend reports the end. The list is virtualized, so the cost of
  // holding every loaded row is memory only — the DOM keeps just the visible window.
  useEffect(() => {
    if (hasMore && !isLoading && nextCursor) {
      loadWebUsers(nextCursor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isLoading, nextCursor]);

  // Full-height spinner for the first page, matching MetricsTabel and the tracks list.
  if (isLoading && users.length === 0) {
    return (
      <div className='spinner-container' style={{marginTop: 'auto', marginBottom: 'auto', width: '100%'}}>
        <MDSpinner size={100} singleColor={spinnerColor}/>
      </div>
    );
  }

  if (users.length === 0) {
    return <div className={'users-empty'}>No users for the selected period</div>;
  }

  return (
    <div className={'reels-users-tabel'}>
      <div className={'header-tabel'}>
        <div className={'users-email-column header-right-border'}>Email</div>
        <div className={'users-date-column header-right-border'}>Created</div>
        <div className={'users-status-column header-right-border'}>Purchase</div>
        <div className={'users-date-column'}>Status changed</div>
      </div>

      {/* The generic is explicit: react-window supplies index and style itself, so inferring the
          row props from the component would demand them in rowProps as well. */}
      <List<{users: WebUserRow[]}>
        rowComponent={Row}
        rowCount={users.length}
        rowHeight={ROW_HEIGHT}
        rowProps={{users}}
        style={{height: 'calc(100vh - 220px)', width: '100%'}}
      />

      {/* Subsequent pages: a small inline spinner under the table, so the rows already loaded stay
          visible and readable while the rest arrives. */}
      {isLoading && (
        <div className={'users-loading-more'}>
          <MDSpinner size={20} singleColor={spinnerColor}/>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  users: state.users.users,
  isLoading: state.users.isLoading,
  hasMore: state.users.hasMore,
  nextCursor: state.users.nextCursor,
});

const mapDispatchToProps = (dispatch: any) => ({
  loadWebUsers: bindActionCreators(loadWebUsers, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(UsersTable);
